import { useMemo, useEffect, useState } from 'react';
import { Button, Popover, Tag } from '@servicetitan/design-system';
import { Flex, Text, Tooltip } from '@servicetitan/anvil2';
import getDisplayName from '@lib/utils/getDisplayName';
import useService from '@modules/ServiceModule/useService';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { WebphoneCallState } from '@modules/SoftPhoneModule/types';
import TasksModule from '@modules/TasksModule';
import useRecording from '@app/layouts/MainLayout/components/CallBar/hooks/useRecording';
import { useCallDuration } from '@app/layouts/MainLayout/components/CallBar/hooks/useCallDuration';
import { getCallTimerStart } from '@app/layouts/MainLayout/components/CallBar/hooks/callTimerRegistry';
import CallControlKeypad from '@app/layouts/MainLayout/components/CallBar/components/CallControlKeypad/CallControlKeypad';
import { closePipWindow } from '../state';
import { PIP_EVENTS } from '../constants';
import styles from '../styles/pip.module.css';

interface PipCallBarProps {
  readonly callState: WebphoneCallState;
  readonly show: boolean;
}

export function PipCallBar({ callState, show }: PipCallBarProps) {
  const {
    callId,
    direction,
    established,
    hasSession,
    held,
    isAudioMuted,
    remoteIdentity,
    remoteCallId,
  } = callState;

  const { userByContactCenterUserId } = useService();
  const isInCall = direction === 'terminating' ? established : hasSession;
  const task = TasksModule.getTaskByCallId(callId);

  const identity = useMemo(
    () =>
      getDisplayName(
        task?.displayName ?? remoteIdentity,
        userByContactCenterUserId,
      ),
    [task?.displayName, remoteIdentity, userByContactCenterUserId],
  );

  const { stop, start, formattedTime } = useCallDuration();

  useEffect(() => {
    if (isInCall) {
      start(getCallTimerStart(callId));
    } else {
      stop();
    }
  }, [isInCall, callId, start, stop]);

  const {
    isRecording,
    isRecordingDisabled,
    isRecordingLoading,
    recordingTooltip,
    toggleRecording,
  } = useRecording(callId);

  const [isKeypadOpen, setIsKeypadOpen] = useState(false);

  if (!callState || !isInCall || !show) {
    return null;
  }

  return (
    <Flex direction='column' gap='2' className={styles.pipCallBar}>
      {/* Info row */}
      <div className={styles.pipCallBarInfo}>
        <Text
          variant='body'
          size='small'
          className='c-white'
          style={{ fontWeight: 600 }}
        >
          {identity}
        </Text>
        <Flex alignItems='center' gap='1'>
          {isRecording && (
            <Tag color='critical' icon='radio_button_checked' className='fs-1'>
              REC
            </Tag>
          )}
          <Text variant='body' size='small' className='c-neutral-10'>
            {formattedTime}
          </Text>
          {held && <Tag className={`${styles.onHoldTag} fs-1`}>On Hold</Tag>}
        </Flex>
      </div>

      {/* Buttons row */}
      <div className={styles.pipCallBarButtons}>
        <Tooltip>
          <Tooltip.Trigger>
            <Button
              aria-label={isAudioMuted ? 'Unmute' : 'Mute'}
              iconName={isAudioMuted ? 'mic_off' : 'mic'}
              className={
                isAudioMuted ? styles.btnActiveCritical : styles.btnDefault
              }
              onClick={() =>
                isAudioMuted
                  ? SoftPhoneModule.callControl.unmute()
                  : SoftPhoneModule.callControl.mute()
              }
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{isAudioMuted ? 'Unmute' : 'Mute'}</Tooltip.Content>
        </Tooltip>
        <div className={styles.hideOnSmall}>
          <Tooltip>
            <Tooltip.Trigger>
              <Button
                aria-label='Transfer'
                iconName='phone_forwarded'
                className={styles.btnDefault}
                onClick={() => {
                  closePipWindow();
                  window.focus();
                  window.dispatchEvent(
                    new CustomEvent(PIP_EVENTS.OPEN_TRANSFER, {
                      detail: callId,
                    }),
                  );
                }}
              />
            </Tooltip.Trigger>
            <Tooltip.Content>Transfer</Tooltip.Content>
          </Tooltip>
        </div>
        <Tooltip>
          <Tooltip.Trigger>
            <span>
              <Button
                aria-label={isRecording ? 'Stop Recording' : 'Start Recording'}
                iconName='radio_button_checked'
                className={
                  isRecording ? styles.btnActiveCritical : styles.btnDefault
                }
                disabled={isRecordingDisabled || isRecordingLoading}
                onClick={() => toggleRecording(remoteCallId)}
              />
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content style={{ maxWidth: 200, fontSize: 12 }}>
            {recordingTooltip ??
              (isRecording ? 'Stop Recording' : 'Start Recording')}
          </Tooltip.Content>
        </Tooltip>
        <Tooltip>
          <Tooltip.Trigger>
            <Button
              aria-label={held ? 'Resume' : 'Hold'}
              iconName='pause'
              className={held ? styles.btnActive : styles.btnDefault}
              onClick={() =>
                held
                  ? SoftPhoneModule.callControl.unhold(callId)
                  : SoftPhoneModule.callControl.hold()
              }
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{held ? 'Resume' : 'Hold'}</Tooltip.Content>
        </Tooltip>
        <div className={styles.hideOnSmall}>
          <Popover
            className='bg-neutral-500 border-style-none'
            open={isKeypadOpen}
            trigger={
              <Tooltip>
                <Tooltip.Trigger>
                  <Button
                    aria-label='Keypad'
                    iconName='dialpad'
                    className={
                      isKeypadOpen ? styles.btnActive : styles.btnDefault
                    }
                    onClick={() => setIsKeypadOpen(!isKeypadOpen)}
                  />
                </Tooltip.Trigger>
                <Tooltip.Content>Keypad</Tooltip.Content>
              </Tooltip>
            }
          >
            <CallControlKeypad
              open={isKeypadOpen}
              onClose={() => setIsKeypadOpen(false)}
            />
          </Popover>
        </div>
        <Tooltip>
          <Tooltip.Trigger>
            <Button
              aria-label='End Call'
              iconName='call_end'
              color='red'
              onClick={() => SoftPhoneModule.callControl.hangup(callId)}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>End Call</Tooltip.Content>
        </Tooltip>
      </div>
    </Flex>
  );
}
